import React, { FC } from "react";

import {
  Color,
  createTheme,
  PaletteMode,
  SxProps,
  ThemeProvider,
} from "@mui/material";
import { Palette, Theme } from "@mui/material/styles";
import {
  CommonColors,
  PaletteAugmentColorOptions,
  PaletteColor,
  PaletteTonalOffset,
  TypeAction,
  TypeBackground,
  TypeDivider,
  TypeText,
} from "@mui/material/styles/createPalette";
import {
  Typography,
  TypographyOptions,
} from "@mui/material/styles/createTypography";

export type Modify<T, R> = Omit<T, keyof R> & R;

const hexToRgbA = (hexCode: string, opacity = 1) => {
  let hex = hexCode.replace("#", "");

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  /* Backward compatibility for whole number based opacity values. */
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }

  return `rgba(${r},${g},${b},${opacity})`;
};

export const ColorTestPilot = {
  blue: "#313C68",
  hoverBlue: "#273053",
  orange: "#FD6B02",
};

export const ColorPrimary = {
  main: "#2962FF",
  dark: "#0013BC",
  light: "#2979FF",
  light2: "#448AFF",
  light3: "#82B1FF",
  light4: "#ECF0FF",
  light5: "#E0E6F5",
};

export const ColorBlack = {
  light: "#1E1E1E",
  main: "#1E1E1E",
  dark: "#1E1E1E",
  contrastText: "#1E1E1E",
};

export const ColorDisabled = {
  light: "#E1E1E1",
  main: "#BBBBBB",
  dark: "#BBBBBB",
  contrastText: "#BBBBBB",
};

export const ColorSecondary = {
  main: "#494C50",
  dark: "#1E1E1E",
  light: "#BBBBBB",
};

export const ColorTextPrimary = ColorSecondary.main;

export const ColorSelectionHighlight = "#F9FAFE";

export const ColorBackground = "#F5F5F5";

export const ColorSuccess = {
  main: "#2CAF2C",
  dark: "#248C24",
  light: "#7AD17A",
  success1: "#41CC2C",
};

export const ColorError = {
  main: "#DC1E38",
  dark: "#C11A31",
  light: "#F88078",
  light2: "#ED8F9D",
};

export const ColorWarning = {
  main: "#FEB52B",
  dark: "#F47500",
  light: "#FECA67",
  info: "#FEAA0B",
};

export const BoxShadow = "0px 4px 8px rgba(140, 140, 140, 0.08)";

export const ColorPurple = "#9C4DDD";

export const ColorGray = {
  light: "#BBBBBB", // border separators, horizontal lines, etc.
  main: "#494C50",
  dark: ColorSecondary.main,
  contrastText: "#757575",
  light1: "#F6F6F6",
  mid: "#D9D9D9",
};

export const ColorSubtitleText = "#909090";
export const ColorInfo = {
  main: "#BBBBBB",
  dark: "#494C50",
  light: "#F1F1F1",
};

export const SUCCESS = {
  color: ColorSecondary.main,
  backgroundColor: hexToRgbA(ColorSuccess.success1, 0.2),
  fontWeight: 400,
};

export const DISABLED = {
  color: ColorSecondary.main,
  backgroundColor: hexToRgbA(ColorDisabled.main, 0.3),
  fontWeight: 400,
};

export const INFO = {
  color: ColorSecondary.main,
  backgroundColor: ColorPrimary.light4,
  fontWeight: 400,
};

export const ERROR = {
  color: "white",
  backgroundColor: ColorError.light,
  // backgroundColor: hexToRgbA(theme.palette.error.main, 0.65),
  fontWeight: 400,
};

export const MIXED_ERROR = {
  color: ColorSecondary.main,
  backgroundColor: "#F38550",
  fontWeight: 400,
};
export const WARNING = {
  color: ColorSecondary.main,
  backgroundColor: ColorWarning.light,
  fontWeight: 400,
};

export const VariationColors = [
  ColorPrimary.dark,
  ColorSuccess.main,
  ColorPrimary.main,
  ColorPurple,
  ColorWarning.main,
  ColorPrimary.light,
];

/**
 * @see Theme
 * @see IgCustomTheme
 */
export type IgCustomTheme = Modify<
  Theme,
  {
    palette: {
      common: CommonColors;
      mode: PaletteMode;
      contrastThreshold: number;
      tonalOffset: PaletteTonalOffset;
      primary: PaletteColor;
      secondary: PaletteColor;
      error: PaletteColor;
      warning: PaletteColor;
      info: PaletteColor;
      success: PaletteColor;
      grey: Color;
      text: TypeText;
      divider: TypeDivider;
      action: TypeAction;
      background: TypeBackground;
      getContrastText: (background: string) => string;
      augmentColor: (options: PaletteAugmentColorOptions) => PaletteColor;

      // IG CUSTOM
      black: Palette["primary"];
      gray: Palette["primary"];
      disabled: Palette["primary"];
    };
    paper: object;
    pageContent: object;
    page: object;
  }
>;

export function createCustomTheme(): IgCustomTheme {
  const baseTheme = createTheme({
    // SEE https://intelligems-io.slack.com/archives/C01TCP522RK/p1654200270639659
    palette: {
      primary: ColorPrimary,
      secondary: ColorSecondary,
      error: ColorError,
      warning: ColorWarning,
      info: ColorInfo,
      success: ColorSuccess,
      text: {
        primary: ColorTextPrimary,
      },
    },
    typography: {
      body1: {
        // lineHeight: "22px",
        fontSize: "16px",
      },
      body2: {
        // lineHeight: "19px",
        fontSize: "14px",
      },
      allVariants: {
        color: ColorTextPrimary,
        // letterSpacing: "0.38px",
        // wordSpacing: "1px",
      },
      fontFamily: ['"Source Sans 3"', "sans-serif", "-apple-system"].join(","),
    },
    components: {
      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: "0px",
            paddingBottom: "0px",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "15px",
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: "small",
          color: "secondary",
        },
      },
      MuiAccordion: {
        defaultProps: {
          TransitionProps: {
            unmountOnExit: true,
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: "small",
        },
      },
      MuiInput: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontSize: "14px",
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: "rgb(0 0 0 / 12%) 1px 2px 3px !important",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          root: {
            borderRadius: "15px",
          },
          paper: {
            boxShadow: "rgb(0 0 0 / 12%) 1px 2px 3px !important",
            borderRadius: "15px",
            border: "1px solid rgba(0, 0, 0, 0.12)",
          },
          list: {
            borderRadius: "15px",
            textDecoration: "none",
            color: "#000",
            textTransform: "none",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&:hover": { backgroundColor: "#F1F1F1" },
            "&.Mui-selected": {
              backgroundColor: "#F1F1F1 !important",
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            marginLeft: "190px",
          },
        },
      },
    },
  });

  return {
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      black: ColorBlack,
      gray: ColorGray,
      disabled: ColorDisabled,
    },
    paper: {
      borderRadius: "15px",
    },
    pageContent: {
      maxWidth: "1450px",
    },
    page: {
      maxWidth: "1600px",
    },
  };
}

interface LibraryThemeProviderProps {
  children?: any; // TODO
  theme: Theme;
}

export const IgTheme = createCustomTheme();

export const IntelligemsThemeProvider: FC<LibraryThemeProviderProps> = ({
  children,
  theme,
}) => <ThemeProvider theme={theme}>{children}</ThemeProvider>;

export type SxProp = SxProps<typeof IgTheme>;
export type Styles = Record<string, SxProp>;
